/**
 * The module strip — one table, driving both the nav and each page's masthead.
 *
 * The eight entries, their two-digit indices and their labels are taken verbatim from the
 * `.modnav` of `design-drafts/reporting-page-v1.html`. Keeping them in one place is what
 * stops the nav and the breadcrumb disagreeing about what a page is called.
 *
 * Every module in the strip is built. `Umpire Appointments` was the last entry that said it
 * was not: run 34 built `03-appointments.md` Part B at `/appointments/umpires` — the
 * supervision surface, its two datasets and the advisory draft — so `built` is true and the
 * nav no longer warns. `Officials Profiles` was a half-built module whose entry said so; both
 * halves are now built — the roster at `/officials` (Part A) and the individual profile at
 * `/officials/:officialId` (Part B).
 *
 * **One kicker per module, and where that is not enough.** A module with two screens whose
 * specs give them different kickers keeps the landing page's line here, and the other screen
 * states its own through `usePageKicker` (`./pageKicker.tsx`).
 */

export interface ModuleEntry {
  /** The two-digit index the nav prints in mono. */
  idx: string;
  label: string;
  path: string;
  /** The masthead H1's thin half, and the breadcrumb's leaf. */
  title: string;
  /** The masthead kicker, verbatim from the module's spec where one exists. */
  kicker: string;
  /** False until the module is built. */
  built: boolean;
}

export const COMMAND_DESK: ModuleEntry = {
  idx: "00",
  label: "Command Desk",
  path: "/",
  title: "Command Desk",
  kicker: "One Screen · Everything The Tournament Needs Of You Today",
  built: true,
};

export const MODULES: readonly ModuleEntry[] = [
  COMMAND_DESK,
  {
    idx: "01",
    label: "TO Appointments",
    path: "/appointments/technical",
    title: "TO Appointments",
    kicker: "Technical Officials · Every Match, Every Competition",
    built: true,
  },
  {
    idx: "02",
    label: "Umpire Appointments",
    path: "/appointments/umpires",
    title: "Umpire Appointments",
    kicker: "Umpires And Video Umpires · Every Match, Every Competition",
    built: true,
  },
  {
    idx: "03",
    label: "Fixtures",
    path: "/fixtures",
    title: "Fixtures",
    // `02-fixtures.md` gives the module its own kicker, and the mockup prints it: the strip's
    // earlier placeholder line was shorter than the page it named. The mockup wins.
    kicker: "All Fixtures · Men's #1916 And Women's #1917 · Live TMS Match Status",
    built: true,
  },
  {
    idx: "04",
    label: "Disciplinary Hearings",
    path: "/hearings",
    title: "Disciplinary Hearings",
    // `06-hearings.md` gives this module a longer kicker than the strip's placeholder ever
    // carried. The page claims spec 06's own line through `usePageKicker`; this stays as the
    // strip's default so the nav entry and the masthead agree before the page mounts.
    kicker: "Code Of Conduct Cases · Report · Natural Justice · Hearing · Decision · Penalty",
    // The first slice — the case record and the document pipeline — is built. What is not
    // built (document generation, the bound catalogue, evidence, the decision record, the
    // sanction tracker, the legal clocks) is stated on the page itself rather than by a nav
    // entry that would call the whole module missing.
    built: true,
  },
  {
    idx: "05",
    label: "Officials Profiles",
    path: "/officials",
    title: "Officials Profiles",
    // `04-officials-profiles.md` §A2 — the roster's own kicker, and the roster is this
    // module's landing page, so the page states nothing of its own and this line is the one
    // copy. The individual profile at `/officials/:officialId` composes §B's kicker from the
    // official it fetched and claims it through `usePageKicker`.
    kicker: "The Tournament Roster · Technical Panel And Umpiring Panel",
    built: true,
  },
  {
    idx: "06",
    label: "Reporting",
    path: "/marking",
    title: "Reporting",
    // `05-reporting-marking.md` §A — the Reporting page's own kicker. The marking screen at
    // `/marking/:officialId` states §B3a's `Final Marking · Whole Tournament` for itself;
    // until this slice one line here had to serve both, and could only be right for one.
    kicker: "Report Progress Across The Whole Panel · Open Any Official To Write Theirs",
    built: true,
  },
  {
    idx: "07",
    label: "Tournament Config",
    path: "/configuration",
    title: "Tournament Config",
    kicker: "What Varies By Tournament Is Data, Not Code",
    built: true,
  },
];

/** The module a pathname belongs to — longest matching path wins, so `/marking/4334` is Reporting. */
export function moduleForPath(pathname: string): ModuleEntry {
  let best: ModuleEntry = COMMAND_DESK;
  for (const entry of MODULES) {
    if (entry.path === "/") continue;
    if (pathname === entry.path || pathname.startsWith(`${entry.path}/`)) {
      if (entry.path.length > best.path.length || best === COMMAND_DESK) best = entry;
    }
  }
  return best;
}

/** The FIH TD/MM/UM report form, linked from the masthead (§A, Alfred 2026-08-04). */
export const TOURNAMENT_REPORT_URL = "https://fih.formstack.com/forms/td_mm_um_report";
